import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BedDouble, BellRing, Check, Circle, IndianRupee, Layers, Pencil, Plus, Trash2, UserPlus, X, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FoodMenuTab } from "@/components/FoodMenuTab";
import { api, errText } from "@/lib/api";

const rupees = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

export default function StayManage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [newFloor, setNewFloor] = useState("");
  const [bedTarget, setBedTarget] = useState(null);
  const [tenant, setTenant] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/stays/${id}`);
      setData(res.data);
    } catch (err) {
      toast.error(errText(err));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const call = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const addFloor = (e) => {
    e.preventDefault();
    if (!newFloor.trim()) return toast.error("Enter a floor name");
    call(async () => {
      await api.post(`/stays/${id}/floors`, { name: newFloor });
      setNewFloor("");
    }, "Floor added");
  };

  const renameThing = (label, current, save) => {
    const value = window.prompt(`Rename ${label}`, current);
    if (value && value.trim() && value !== current) call(() => save(value.trim()), `${label} renamed`);
  };

  const openBed = (bed) => {
    setBedTarget(bed);
    setTenant({
      label: bed.label || "",
      rent: bed.rent ?? "",
      tenant_name: bed.tenant_name || "",
      tenant_phone: bed.tenant_phone || "",
      tenant_email: bed.tenant_email || "",
      tenant_joined_on: bed.tenant_joined_on || "",
      notes: bed.notes || "",
      rent_paid: !!bed.rent_paid,
      paid_for_month: bed.paid_for_month || "",
      reminders_off: !!bed.reminders_off,
    });
  };

  const saveBed = () =>
    call(async () => {
      await api.put(`/stay/beds/${bedTarget.bed_id}`, {
        ...tenant,
        rent: tenant.rent === "" ? null : Number(tenant.rent),
        paid_for_month: tenant.paid_for_month || null,
      });
      setBedTarget(null);
    }, "Bed updated");

  const shareBedLink = async (bed) => {
    const place = [bed.floor_name, bed.room_name, bed.label].filter((x) => x && x !== "—").join(" · ");
    try {
      const { data } = await api.post(`/stay/beds/${bed.bed_id}/onboard-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success("Link copied — send it to the tenant on WhatsApp");
      } catch {
        toast.success(data.url);
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(
        `${data?.stay?.name || "Our stay"} — please fill your details for ${place}: ${data.url}`)}`, "_blank", "noopener");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const decidePending = (bed, approve) =>
    call(() => api.post(`/stay/beds/${bed.bed_id}/onboard-${approve ? "approve" : "reject"}`),
      approve ? "Tenant added to this bed" : "Submission discarded");

  const toggleRent = (bed) =>
    call(() => api.patch(`/stay/beds/${bed.bed_id}/rent`, { rent_paid: !bed.rent_paid }),
      bed.rent_paid ? "Marked as unpaid" : "Rent marked paid");

  const remindBed = async (bed) => {
    try {
      const { data } = await api.post(`/stay/beds/${bed.bed_id}/remind`);
      toast.success(data.status === "sent"
        ? `Rent reminder emailed to ${bed.tenant_name || bed.tenant_email}`
        : "A reminder was already sent for this month");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({});

  const openPayDetails = () => {
    const s = data.stay;
    setPayForm({
      upi_id: s.upi_id || "",
      bank_account_name: s.bank_account_name || "",
      bank_account_number: s.bank_account_number || "",
      bank_ifsc: s.bank_ifsc || "",
      payment_note: s.payment_note || "",
    });
    setPayOpen(true);
  };

  const savePayDetails = () =>
    call(async () => {
      const s = data.stay;
      await api.put(`/stays/${id}`, {
        name: s.name, city: s.city, locality: s.locality || "", address: s.address || "",
        contact_phone: s.contact_phone || "", default_rent: Number(s.default_rent || 0),
        rent_due_day: Number(s.rent_due_day || 5), notes: s.notes || "",
        kind: s.kind || "pg", property_id: s.property_id || "", ...payForm,
      });
      setPayOpen(false);
    }, "Payment details saved");

  if (!data)
    return (
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );

  const { stay, floors, rows, stats } = data;
  const isRental = stay.kind === "rental";
  const L = isRental
    ? { back: "All rentals", backTo: "/rentals", tenants: "Tenants & rent", structure: "Rooms", unit: "Tenant slot", unitPlural: "Tenant slots" }
    : { back: "All stays", backTo: "/stays", tenants: "Beds & tenants", structure: "Floors & rooms", unit: "Bed", unitPlural: "Beds" };

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10" data-testid="stay-manage-page">
      <Link to={L.backTo} className="inline-flex items-center gap-2 text-[13px] font-semibold hover:underline" data-testid="stay-back-link">
        <ArrowLeft className="h-4 w-4" /> {L.back}
      </Link>
      <h1 className="mt-3 font-display text-3xl">{stay.name}</h1>
      <p className="mt-1 text-[13px] text-muted-foreground">
        {[stay.locality, stay.city].filter(Boolean).join(", ")}
        {stay.contact_phone ? ` · ${stay.contact_phone}` : ""} · {isRental ? "rent" : "default rent"} {rupees(stay.default_rent)} · rent due on
        day {stay.rent_due_day || 5}
      </p>
      <button
        onClick={openPayDetails}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-[12px] font-bold transition-colors duration-200 hover:border-foreground"
        data-testid="stay-payment-details-btn"
      >
        <IndianRupee className="h-3.5 w-3.5 text-primary" />
        {stay.upi_id || stay.bank_account_number ? "Edit UPI / bank details" : "Add UPI / bank details"}
      </button>

      <div className="mt-8 grid gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="stay-stats">
        {(isRental
          ? [
              { label: "Rooms", value: stats.rooms },
              { label: L.unitPlural, value: stats.beds },
              { label: "Occupied", value: stats.occupied },
              { label: "Vacant", value: stats.vacant },
              { label: "Rent paid", value: stats.paid },
              { label: "Rent due", value: stats.unpaid },
            ]
          : [
              { label: "Floors", value: stats.floors },
              { label: "Rooms", value: stats.rooms },
              { label: "Beds", value: stats.beds },
              { label: "Occupied", value: stats.occupied },
              { label: "Rent paid", value: stats.paid },
              { label: "Rent due", value: stats.unpaid },
            ]
        ).map((s) => (
          <div key={s.label} className="rounded-2xl border border-border p-5">
            <p className="font-display text-2xl">{s.value}</p>
            <p className="mt-1 text-[12px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 text-[13px] text-muted-foreground">
        Collected {rupees(stats.collected_rent)} of {rupees(stats.expected_rent)} expected this month
        {stats.arrears > 0 ? ` · ${rupees(stats.arrears)} outstanding across all pending months` : ""}
      </p>

      <Tabs defaultValue="dashboard" className="mt-8">
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar rounded-full bg-muted p-1">
          <TabsTrigger value="dashboard" className="rounded-full px-5" data-testid="stay-tab-dashboard">{L.tenants}</TabsTrigger>
          <TabsTrigger value="structure" className="rounded-full px-5" data-testid="stay-tab-structure">{L.structure}</TabsTrigger>
          {!isRental && (
            <TabsTrigger value="food" className="rounded-full px-5" data-testid="stay-tab-food">Food menu</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="dashboard" className="mt-7">
          <div className="rounded-2xl border border-border overflow-x-auto">
            <Table className="min-w-[760px]" data-testid="stay-beds-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Rent</TableHead>
                  <TableHead>{L.unit}</TableHead>
                  <TableHead>Room</TableHead>
                  {!isRental && <TableHead>Floor</TableHead>}
                  <TableHead>Tenant</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Since</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-sm text-muted-foreground">
                      {isRental
                        ? `No ${L.unitPlural.toLowerCase()} yet — add one from the "${L.structure}" tab.`
                        : 'No beds yet — add floors, rooms and beds from the "Floors & rooms" tab.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((b) => (
                    <TableRow key={b.bed_id} data-testid={`stay-bed-row-${b.bed_id}`}>
                      <TableCell>
                        <button
                          onClick={() => b.occupied && toggleRent(b)}
                          className="flex items-center gap-2"
                          title={b.occupied ? "Toggle rent paid" : "Vacant bed"}
                          data-testid={`stay-rent-signal-${b.bed_id}`}
                        >
                          <Circle
                            className={`h-3.5 w-3.5 ${
                              !b.occupied
                                ? "fill-muted-foreground/40 text-muted-foreground/40"
                                : b.rent_paid
                                ? "fill-emerald-500 text-emerald-500"
                                : "fill-red-500 text-red-500"
                            }`}
                          />
                          <span className="text-[12px] font-semibold">
                            {!b.occupied ? "Vacant" : b.rent_paid ? "Paid" : "Due"}
                          </span>
                        </button>
                      </TableCell>
                      <TableCell className="text-sm font-semibold">{b.label}</TableCell>
                      <TableCell className="text-sm">{b.room_name}</TableCell>
                      {!isRental && <TableCell className="text-sm">{b.floor_name}</TableCell>}
                      <TableCell className="text-sm">{b.tenant_name || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {b.tenant_phone || "—"}
                        {(b.owed_months?.length || 0) > 1 && (
                          <span
                            className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-bold text-destructive"
                            data-testid={`stay-arrears-badge-${b.bed_id}`}
                          >
                            {b.owed_months.length} months due
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{rupees(b.rent ?? stay.default_rent)}</TableCell>
                      <TableCell className="text-sm">{b.tenant_joined_on || "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-0.5">
                          {b.pending_tenant?.name && (
                            <span className="mr-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary" data-testid={`stay-pending-badge-${b.bed_id}`}>
                              Form filled
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-lg"
                            onClick={() => openBed(b)}
                            data-testid={`stay-edit-bed-${b.bed_id}`}
                            title="Bed & tenant details"
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                          {!b.occupied && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-lg"
                              onClick={() => shareBedLink(b)}
                              data-testid={`stay-share-bed-${b.bed_id}`}
                              title="Share a form link with the tenant"
                            >
                              <Link2 className="h-4 w-4" />
                            </Button>
                          )}
                          {b.occupied && !b.rent_paid && (b.tenant_email || "").trim() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-lg"
                              onClick={() => remindBed(b)}
                              data-testid={`stay-remind-bed-${b.bed_id}`}
                              title="Email a rent reminder now"
                            >
                              <BellRing className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="structure" className="mt-7">
          {!isRental && (
          <form onSubmit={addFloor} className="flex flex-wrap items-end gap-3" data-testid="stay-add-floor-form">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">New floor name</Label>
              <Input
                value={newFloor}
                onChange={(e) => setNewFloor(e.target.value)}
                placeholder="Ground floor"
                className="h-12 w-56 rounded-lg"
                data-testid="stay-floor-name-input"
              />
            </div>
            <Button type="submit" className="h-12 rounded-lg font-bold hover:brightness-95" data-testid="stay-add-floor-btn">
              <Plus className="h-4 w-4 mr-2" /> Add floor
            </Button>
          </form>
          )}

          <div className="mt-7 space-y-5">
            {floors.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="stay-no-floors">No floors yet.</p>
            )}
            {floors.map((f) => (
              <div key={f.floor_id} className="rounded-2xl border border-border p-6" data-testid={`stay-floor-${f.floor_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="flex items-center gap-2 font-display text-lg">
                    <Layers className="h-4 w-4 text-primary" /> {f.name}
                    <span className="text-[12px] font-normal text-muted-foreground">({f.rooms.length} rooms)</span>
                  </p>
                  <div className="flex gap-1">
                    {!isRental && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-lg"
                      onClick={() => renameThing("floor", f.name, (v) => api.put(`/stay/floors/${f.floor_id}`, { name: v }))}
                      data-testid={`stay-rename-floor-${f.floor_id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-lg"
                      onClick={() => {
                        const name = window.prompt("Room name / number", `Room ${f.rooms.length + 1}`);
                        if (name?.trim()) call(() => api.post(`/stay/floors/${f.floor_id}/rooms`, { name: name.trim() }), "Room added");
                      }}
                      data-testid={`stay-add-room-${f.floor_id}`}
                    >
                      <Plus className="h-4 w-4 mr-1.5" /> Room
                    </Button>
                    {!isRental && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-lg text-destructive"
                      onClick={() => call(() => api.delete(`/stay/floors/${f.floor_id}`), "Floor deleted")}
                      data-testid={`stay-delete-floor-${f.floor_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {f.rooms.map((r) => (
                    <div key={r.room_id} className="rounded-xl border border-border p-4" data-testid={`stay-room-${r.room_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-bold">{r.name}</p>
                          <p className="text-[12px] text-muted-foreground">
                            {r.beds.length} {isRental ? "tenant slots" : "beds"}{r.rent ? ` · ${rupees(r.rent)}${isRental ? "" : " / bed"}` : ""}
                          </p>
                        </div>
                        <div className="flex gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 rounded-lg px-2"
                            onClick={() =>
                              renameThing("room", r.name, (v) => api.put(`/stay/rooms/${r.room_id}`, { name: v, rent: r.rent }))
                            }
                            data-testid={`stay-rename-room-${r.room_id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 rounded-lg px-2 text-destructive"
                            onClick={() => call(() => api.delete(`/stay/rooms/${r.room_id}`), "Room deleted")}
                            data-testid={`stay-delete-room-${r.room_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {r.beds.map((b) => (
                          <button
                            key={b.bed_id}
                            onClick={() => openBed(b)}
                            className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-[12px] font-semibold hover:border-foreground transition-colors duration-200"
                            data-testid={`stay-bed-chip-${b.bed_id}`}
                          >
                            <BedDouble className="h-3.5 w-3.5" /> {b.label}
                            <Circle
                              className={`h-2.5 w-2.5 ${
                                !(b.tenant_name || "").trim()
                                  ? "fill-muted-foreground/40 text-muted-foreground/40"
                                  : b.rent_paid
                                  ? "fill-emerald-500 text-emerald-500"
                                  : "fill-red-500 text-red-500"
                              }`}
                            />
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            const label = window.prompt(isRental ? "Tenant slot name" : "Bed number / name",
                              isRental ? `Tenant ${r.beds.length + 1}` : `Bed ${r.beds.length + 1}`);
                            if (label?.trim())
                              call(() => api.post(`/stay/rooms/${r.room_id}/beds`, { label: label.trim() }), isRental ? "Tenant slot added" : "Bed added");
                          }}
                          className="flex items-center gap-1.5 rounded-full border border-dashed border-input px-3 py-1.5 text-[12px] font-semibold hover:border-foreground transition-colors duration-200"
                          data-testid={`stay-add-bed-${r.room_id}`}
                        >
                          <Plus className="h-3.5 w-3.5" /> {isRental ? "Tenant slot" : "Bed"}
                        </button>
                      </div>
                    </div>
                  ))}
                  {f.rooms.length === 0 && <p className="text-[13px] text-muted-foreground">No rooms on this floor yet.</p>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {!isRental && (
          <TabsContent value="food" className="mt-7">
            <FoodMenuTab stayId={id} />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!bedTarget} onOpenChange={(v) => !v && setBedTarget(null)}>
        <DialogContent
          className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto"
          data-testid="stay-bed-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-lg">{isRental ? "Tenant & rent details" : "Bed & tenant details"}</DialogTitle>
            <DialogDescription className="text-[13px]">
              {isRental
                ? "Add the tenant living in this unit, set the monthly rent and mark it paid or due."
                : "Assign a tenant to this bed, set the rent and mark it paid or due."}
            </DialogDescription>
          </DialogHeader>

          {bedTarget?.pending_tenant?.name && (
            <div className="rounded-xl border border-primary/40 bg-primary/5 p-4" data-testid="stay-pending-panel">
              <p className="text-[12px] font-bold uppercase tracking-wide text-primary">Tenant filled the form</p>
              <p className="mt-2 text-sm font-semibold">{bedTarget.pending_tenant.name}</p>
              <p className="text-[13px] text-muted-foreground">
                {bedTarget.pending_tenant.phone}
                {bedTarget.pending_tenant.email ? ` · ${bedTarget.pending_tenant.email}` : ""}
                {bedTarget.pending_tenant.joined_on ? ` · moving in ${bedTarget.pending_tenant.joined_on}` : ""}
              </p>
              {bedTarget.pending_tenant.notes && (
                <p className="mt-1.5 text-[13px] text-muted-foreground">"{bedTarget.pending_tenant.notes}"</p>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  className="rounded-lg font-bold"
                  onClick={() => {
                    const bed = bedTarget;
                    setBedTarget(null);
                    decidePending(bed, true);
                  }}
                  data-testid="stay-pending-approve"
                >
                  <Check className="h-4 w-4 mr-1.5" /> Approve &amp; assign
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-lg text-destructive"
                  onClick={() => {
                    const bed = bedTarget;
                    setBedTarget(null);
                    decidePending(bed, false);
                  }}
                  data-testid="stay-pending-reject"
                >
                  Discard
                </Button>
              </div>
            </div>
          )}


          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["label", isRental ? "Slot name" : "Bed number / name"],
              ["rent", "Rent ₹"],
              ["tenant_name", "Tenant name"],
              ["tenant_phone", "Tenant phone"],
              ["tenant_email", "Tenant email"],
              ["tenant_joined_on", "Joined on"],
            ].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold">{label}</Label>
                <Input
                  type={k === "tenant_joined_on" ? "date" : k === "rent" ? "number" : "text"}
                  value={tenant[k] ?? ""}
                  onChange={(e) => setTenant({ ...tenant, [k]: e.target.value })}
                  className="h-11 rounded-lg"
                  data-testid={`stay-bed-${k.replace(/_/g, "-")}-input`}
                />
              </div>
            ))}
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold">Notes</Label>
              <Input
                value={tenant.notes ?? ""}
                onChange={(e) => setTenant({ ...tenant, notes: e.target.value })}
                className="h-11 rounded-lg"
                data-testid="stay-bed-notes-input"
              />
            </div>
          </div>

          <button
            onClick={() => setTenant({ ...tenant, rent_paid: !tenant.rent_paid })}
            className={`mt-2 flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
              tenant.rent_paid ? "border-emerald-500 bg-emerald-500/10 text-emerald-700" : "border-red-400 bg-red-500/5 text-red-600"
            }`}
            data-testid="stay-bed-rent-toggle"
          >
            {tenant.rent_paid ? "Rent paid this month" : "Rent due this month"}
            {tenant.rent_paid ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </button>

          <button
            onClick={() => setTenant({ ...tenant, reminders_off: !tenant.reminders_off })}
            className="mt-2 flex items-center justify-between rounded-xl border border-border px-4 py-3 text-sm font-semibold transition-colors duration-200 hover:border-foreground"
            data-testid="stay-bed-reminders-toggle"
          >
            {tenant.reminders_off ? "Rent reminder emails paused" : "Rent reminder emails on"}
            <BellRing className={`h-4 w-4 ${tenant.reminders_off ? "text-muted-foreground" : "text-primary"}`} />
          </button>

          <div className="mt-3 flex flex-wrap justify-between gap-3">
            <Button
              variant="ghost"
              className="h-11 rounded-lg text-destructive"
              onClick={() =>
                call(async () => {
                  await api.delete(`/stay/beds/${bedTarget.bed_id}`);
                  setBedTarget(null);
                }, "Bed deleted")
              }
              data-testid="stay-bed-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete {isRental ? "slot" : "bed"}
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" className="h-11 rounded-lg" onClick={() => setBedTarget(null)} data-testid="stay-bed-cancel-btn">
                Cancel
              </Button>
              <Button className="h-11 rounded-lg font-bold hover:brightness-95" onClick={saveBed} data-testid="stay-bed-save-btn">
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="stay-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">How tenants pay you</DialogTitle>
            <DialogDescription className="text-[13px]">
              Tenants see these on their rent page and in your WhatsApp message. Nothing is charged through Houses7.com.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            {[
              ["upi_id", "UPI ID", "name@bank"],
              ["bank_account_name", "Account holder name", ""],
              ["bank_account_number", "Account number", ""],
              ["bank_ifsc", "IFSC code", ""],
              ["payment_note", "Note for tenants", "e.g. Please add your bed number in the remark"],
            ].map(([k, label, ph]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold">{label}</Label>
                <Input
                  value={payForm[k] ?? ""}
                  onChange={(e) => setPayForm({ ...payForm, [k]: e.target.value })}
                  placeholder={ph}
                  className="h-11 rounded-lg"
                  data-testid={`stay-payment-${k.replace(/_/g, "-")}-input`}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end gap-3">
            <Button variant="outline" className="h-11 rounded-lg" onClick={() => setPayOpen(false)} data-testid="stay-payment-cancel-btn">
              Cancel
            </Button>
            <Button className="h-11 rounded-lg font-bold hover:brightness-95" onClick={savePayDetails} data-testid="stay-payment-save-btn">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
